import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  SlideAdmin,
  SpecimenAdmin,
  Stain,
  Block,
  Specimen,
  AdminCaseList,
  CaseAdmin,
} from "pwapi";

import {
  fetchBarcodeUrl,
  fetchMacroUrl,
} from "shared/actions/admin/adminSlideActions";
import { slideActions } from "shared/actions/admin/adminSlidesActions";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";
import { fullNameForBlock, sortBlocks } from "shared/utils/blockUtils";
import { slideDetailLabel } from "shared/utils/slideUtils";
import {
  use_pw_source_attributes,
  specimen_source,
  has_source_attributes,
  sortSpecimens,
} from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import TextInput from "components/customInputs/TextInput";
import DjangoLink from "components/DjangoLink";
import Spinner from "components/Spinner";
import { useAppSelector } from "store/hooks";

import { shallowSlideComparison } from "./slideEditFormUtils";
import AdminPanel from "../adminPanel/AdminPanel";
import { AdminFieldCase, AdminFieldStainType } from "../edit/fields";
import { LabOption } from "../edit/ResourceOptions";
import SlideImage from "../SlideImage";

type SlideEditFormProps = {
  adminSlide: SlideAdminWithUrls;
  isNew?: boolean;
  onCancel: MouseEventHandler<HTMLButtonElement>;
  onSubmit: Function;
  onDelete: MouseEventHandler<HTMLButtonElement>;
  onResetAction: Function;
  onRunAction: Function;
};

const enabledStatuses = [
  SlideAdmin.status.RECEIVED_WPL,
  SlideAdmin.status.RECEIVED_LAB,
  SlideAdmin.status.MANUAL_REVIEW,
  SlideAdmin.status.MANUAL_MERGE,
  SlideAdmin.status.SUCCESS,
  SlideAdmin.status.ON_HOLD,
];

const SlideEditForm = ({
  adminSlide,
  isNew,
  onCancel,
  onSubmit,
  onDelete,
  onResetAction,
  onRunAction,
}: SlideEditFormProps) => {
  const [slideAction, setSlideAction] = useState<string>("");
  const dispatch = useDispatch();
  const stains = useAppSelector(({ stains }) => stains);
  const unarchivedStains = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(stains).filter(
          ([_id, s]) =>
            !s.is_archived &&
            (adminSlide.case?.source !== CaseAdmin.source.PW ||
              !s.category?.startsWith("PW-"))
        )
      ),
    [stains, adminSlide.case?.source]
  );

  const antigenRetrievalMethods = useAppSelector(
    ({ antigenRetrievalMethods }) => antigenRetrievalMethods
  );
  const antibodySpecies = useAppSelector(
    ({ antibodySpecies }) => antibodySpecies
  );
  const stain = adminSlide.stain_id ? stains[adminSlide.stain_id] : undefined;

  useEffect(() => {
    if (adminSlide.id) {
      dispatch(fetchBarcodeUrl(adminSlide.id));
      dispatch(fetchMacroUrl(adminSlide.id));
    }
  }, [adminSlide.id]);

  const handleOnSubmit = (values: SlideAdminWithUrls) => {
    const submissionForm = {
      ...shallowSlideComparison(adminSlide, values),
      ...(isNew
        ? {
            case_id: adminSlide.case?.id,
            specimen_id: values?.specimen?.id,
            block_obj_id: values?.block_obj?.id,
          }
        : {
            id: adminSlide.id,
          }),
    };

    onSubmit(submissionForm);
  };

  return (
    <Formik
      initialValues={{
        ...adminSlide,
        status: isNew
          ? adminSlide.case?.source === CaseAdmin.source.WPL
            ? SlideAdmin.status.RECEIVED_WPL
            : SlideAdmin.status.RECEIVED_LAB
          : adminSlide.status,
        is_rescan: adminSlide.is_rescan || false,
        is_control: adminSlide.is_control || false,
        isLoading: undefined,
        actionRequestState: undefined,
      }}
      enableReinitialize
      onSubmit={handleOnSubmit}
    >
      {({ resetForm, values, dirty }) => (
        <Form className="AdminPanelForm">
          <AdminPanel
            title={{
              prefix: isNew
                ? `Add Slide${
                    adminSlide.case?.accession
                      ? ` To ${adminSlide.case?.accession}`
                      : ""
                  }`
                : `Slide #${adminSlide.id}`,
              main: isNew
                ? "Create New Slide"
                : slideDetailLabel(adminSlide, stain, true),
            }}
            djangoLink={
              <DjangoLink
                route={`histoslide/slide${
                  isNew ? "" : `/${adminSlide.id}/change/`
                }`}
                title="Django Slide Access"
              />
            }
            isSaving={adminSlide.isLoading}
          >
            <>
              <div className="panelContainer">
                {adminSlide.slide_review_reason &&
                  adminSlide.status === "manual-review" && (
                    <fieldset className="panelContainer__section panelContainer__messageBox">
                      <legend className="panelContainer__section--title">
                        Review Reason
                      </legend>
                      <span>
                        <strong>Reason for Review:</strong>{" "}
                        {adminSlide.slide_review_reason.replace(/-/g, " ")}
                      </span>
                    </fieldset>
                  )}
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Slide Details
                  </legend>
                  {!isNew && (
                    <>
                      <div className="image__container">
                        <SlideImage
                          imgSrc={adminSlide.barcode_url?.url}
                          altText="slide barcode"
                          isError={adminSlide.barcode_url?.error}
                          errText="Missing Barcode"
                          isFullSize
                        />
                        <SlideImage
                          imgSrc={adminSlide.macro_url?.url}
                          altText="slide macro image"
                          isError={adminSlide.macro_url?.error}
                          errText="Missing Macro Image"
                          isFullSize
                        />
                      </div>
                      <Field name="case">
                        {({ field, form }: any) => (
                          <AdminFieldCase
                            {...field}
                            onChange={(newValue: { object: AdminCaseList }) => {
                              // Try to stick it somewhere by default
                              // The first option in the dropdown isn't a bad idea
                              const block_obj =
                                newValue.object.blocks?.at(0) || null;
                              // If the block exists, set it's specimen too
                              // Otherwise, try at _least_ specimen while keeping block null
                              const specimen = block_obj?.specimen_id
                                ? newValue.object.specimens?.find(
                                    (s) => s.id === block_obj.specimen_id
                                  )
                                : newValue.object.specimens?.at(0) || null;

                              form.setValues({
                                ...form.values,
                                case: newValue.object,
                                ...(newValue.object.id === adminSlide.case?.id
                                  ? {
                                      specimen: adminSlide.specimen,
                                      block_obj: adminSlide.block_obj,
                                    }
                                  : {
                                      // If we couldn't grab a specimen/block_obj above,
                                      // We might set specimen/block_obj to null to clear out the UI,
                                      // This will 500 on the backend, but we can stop the frontend
                                      // from moving forward with it via `required`
                                      // (it's better than moving a case while keeping it's old block/specimen)
                                      specimen,
                                      block_obj,
                                    }),
                              });
                            }}
                          />
                        )}
                      </Field>
                    </>
                  )}
                  <div className="panelContainer__inputGroup">
                    {isNew ? (
                      <CustomInput label="Accession">
                        <div className="flatField">
                          {adminSlide.case?.accession}
                        </div>
                      </CustomInput>
                    ) : (
                      <Field name="accession_number">
                        {({ field }: any) => (
                          <TextInput
                            label="Accession"
                            id="accession_number"
                            {...field}
                          />
                        )}
                      </Field>
                    )}
                    <Field name="status">
                      {({ field }: any) => (
                        <SelectInput
                          label="Status"
                          id="status"
                          required
                          skipBlankOption
                          options={(isNew
                            ? [
                                ...(values.case?.source === CaseAdmin.source.PW
                                  ? [SlideAdmin.status.RECEIVED_LAB]
                                  : []),
                                ...(values.case?.source === CaseAdmin.source.WPL
                                  ? [SlideAdmin.status.RECEIVED_WPL]
                                  : []),
                              ]
                            : [
                                ...(values.case?.source === CaseAdmin.source.PW
                                  ? [SlideAdmin.status.RECEIVED_LAB]
                                  : []),
                                ...(values.case?.source === CaseAdmin.source.WPL
                                  ? [SlideAdmin.status.RECEIVED_WPL]
                                  : []),
                                SlideAdmin.status.MANUAL_REVIEW,
                                SlideAdmin.status.MANUAL_MERGE,
                                SlideAdmin.status.SUCCESS,
                                SlideAdmin.status.ON_HOLD,
                              ]
                          )
                            .concat(
                              adminSlide.status &&
                                !enabledStatuses.includes(adminSlide.status)
                                ? [adminSlide.status]
                                : []
                            )
                            .map((p) => ({
                              label: p,
                              value: p,
                              disabled: !enabledStatuses.includes(p),
                            }))}
                          {...field}
                        />
                      )}
                    </Field>
                  </div>
                  {!isNew && (
                    <CustomInput label="Scanning Lab">
                      <div className="flatField">
                        <LabOption object={adminSlide?.lab} />
                      </div>
                    </CustomInput>
                  )}
                  <Field name="stain_id">
                    {({ field, form }: any) => (
                      <AdminFieldStainType
                        {...field}
                        onChange={(newValue: { object: Stain }) => {
                          form.setFieldValue("stain_id", newValue.object.id);
                        }}
                        required={isNew}
                        allStains={stains}
                        availableStains={unarchivedStains}
                      />
                    )}
                  </Field>
                  {(stains[values.stain_id || 0]?.category ===
                    Stain.category.IHC ||
                    values.antigen_retrieval_method ||
                    values.antibody_species) && (
                    <div className="panelContainer__inputGroup">
                      <Field name="antigen_retrieval_method">
                        {({ field, form }: any) => (
                          <SelectInput
                            label="Antigen Retrieval Method"
                            id="antigen_retrieval_method"
                            defaultLabel=""
                            options={Object.values(antigenRetrievalMethods).map(
                              (arm) => ({
                                label: `${arm.abbreviation} (${arm.name})`,
                                value: arm.id,
                              })
                            )}
                            {...field}
                            value={field.value?.id || ""}
                            onChange={(e) => {
                              form.setFieldValue(
                                "antigen_retrieval_method",
                                antigenRetrievalMethods[Number(e.target.value)]
                              );
                            }}
                          />
                        )}
                      </Field>
                      <Field name="antibody_species">
                        {({ field, form }: any) => (
                          <SelectInput
                            label="Antibody Species"
                            id="antibody_species"
                            defaultLabel=""
                            options={Object.values(antibodySpecies).map(
                              (species) => ({
                                label: `${species.name}${
                                  species.label ? ` (${species.label})` : ""
                                }`,
                                value: species.id,
                              })
                            )}
                            {...field}
                            value={field.value?.id || ""}
                            onChange={(e) => {
                              form.setFieldValue(
                                "antibody_species",
                                antibodySpecies[Number(e.target.value)]
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  )}

                  <div className="panelContainer__inputGroup">
                    <Field name="is_rescan">
                      {({ field, form }: any) => (
                        <label className="panelContainer__checkbox">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={() => {
                              form.setFieldValue(
                                "is_rescan",
                                !form.values.is_rescan
                              );
                            }}
                          />
                          Rescan
                        </label>
                      )}
                    </Field>
                    <Field name="is_control">
                      {({ field, form }: any) => (
                        <label className="panelContainer__checkbox">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={() => {
                              form.setFieldValue(
                                "is_control",
                                !form.values.is_control
                              );
                            }}
                          />
                          Control
                        </label>
                      )}
                    </Field>
                  </div>
                </fieldset>
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    SID
                  </legend>
                  <div className="flatFields">
                    <div
                      className="flatFields"
                      title={
                        values.specimen &&
                        has_source_attributes(values.specimen)
                          ? undefined
                          : `Missing ${
                              values.specimen &&
                              use_pw_source_attributes(values.specimen)
                                ? "Type, Site, or Procedure"
                                : "Source"
                            }`
                      }
                    >
                      {values.specimen &&
                      has_source_attributes(values.specimen) ? (
                        <FontAwesomeIcon color="green" icon="check" />
                      ) : (
                        <FontAwesomeIcon color="red" icon="exclamation" />
                      )}
                      Source
                    </div>
                    <div className="flatFields">
                      <div
                        className="flatFields"
                        title={
                          values.specimen?.clinical_history ||
                          "Missing Clinical History"
                        }
                      >
                        {values.specimen?.clinical_history ? (
                          <FontAwesomeIcon color="green" icon="check" />
                        ) : (
                          <FontAwesomeIcon color="red" icon="exclamation" />
                        )}
                        Clinical History
                      </div>
                    </div>
                    <div
                      className="flatFields"
                      title={
                        values.specimen?.gross_description ||
                        "Missing Gross Description"
                      }
                    >
                      {values.specimen?.gross_description ? (
                        <FontAwesomeIcon color="green" icon="check" />
                      ) : (
                        <FontAwesomeIcon color="red" icon="exclamation" />
                      )}
                      Gross Description
                    </div>
                  </div>
                  <div className="panelContainer__inputGroup">
                    <Field name="specimen">
                      {({ field, form }: any) => {
                        const specimens = sortSpecimens(
                          form.values.case?.specimens || []
                        ) as SpecimenAdmin[];
                        return (
                          <SelectInput
                            label="Specimen"
                            id="specimen"
                            disableDefault
                            options={specimens.map((p) => ({
                              label: p.name,
                              value: p.id,
                            }))}
                            {...field}
                            value={field.value?.id || ""}
                            onChange={(e) => {
                              form.setFieldValue(
                                "specimen",
                                specimens.find(
                                  (v: SpecimenAdmin) =>
                                    `${v.id}` === e.target.value
                                )
                              );
                            }}
                            disabled
                            required={
                              (adminSlide.raw_barcode_text &&
                                adminSlide.case?.accession_prefix
                                  ?.is_diagnostic &&
                                form.values.status === "success" &&
                                !field.value) ||
                              // Require this if we "changed cases"
                              //  ... unless the destination case has no specimen)
                              (adminSlide.case?.id !== form.values.case?.id &&
                                specimens?.length)
                            }
                          />
                        );
                      }}
                    </Field>
                    <Field name="block_obj">
                      {({ field, form }: any) => {
                        const specimens = sortSpecimens(
                          form.values.case?.specimens || []
                        ) as Specimen[];
                        const blocks = sortBlocks(
                          form.values.case?.blocks || [],
                          specimens
                        ) as Block[];
                        return (
                          <SelectInput
                            label="Block"
                            id="block_obj"
                            disableDefault
                            options={blocks.map((block) => {
                              const specimen = specimens.find(
                                (s) => s.id === block.specimen_id
                              );
                              return {
                                label: fullNameForBlock(block, specimen),
                                value: block.id,
                              };
                            })}
                            {...field}
                            value={field.value?.id || ""}
                            onChange={(e) => {
                              const newBlock = blocks.find(
                                (v) => `${v.id}` === e.target.value
                              );
                              form.setValues({
                                ...form.values,
                                block_obj: newBlock,
                                specimen: specimens.find(
                                  (v) => v.id === newBlock?.specimen_id
                                ),
                              });
                            }}
                            required={
                              (adminSlide.raw_barcode_text &&
                                adminSlide.case?.accession_prefix
                                  ?.is_diagnostic &&
                                form.values.status === "success" &&
                                !field.value) ||
                              isNew ||
                              // Require this if we "changed cases"
                              //  ... unless the destination case has no specimen or blocks)
                              (adminSlide.case?.id !== form.values.case?.id &&
                                specimens?.length &&
                                blocks?.length)
                            }
                          />
                        );
                      }}
                    </Field>
                    <Field name="slide_number">
                      {({ field }: any) => (
                        <TextInput
                          label="Slide Number"
                          id="slide_number"
                          required
                          {...field}
                        />
                      )}
                    </Field>
                  </div>
                </fieldset>
                {!isNew && (
                  <>
                    <fieldset className="panelContainer__section qc__container">
                      <legend className="panelContainer__section--title">
                        Specimen {values.specimen?.name}
                      </legend>
                      <div className="specimen__grid">
                        <label>Source:</label>
                        <span>
                          <b>
                            {values.specimen &&
                              specimen_source(values.specimen)}
                          </b>
                        </span>
                        <label>Clinical History:</label>
                        <span>{values.specimen?.clinical_history}</span>
                        <label>Gross Description:</label>
                        <span>{values.specimen?.gross_description}</span>
                      </div>
                    </fieldset>
                    <fieldset className="panelContainer__section qc__container">
                      <legend className="panelContainer__section--title">
                        QC
                      </legend>
                      <Field name="qc_status">
                        {({ field }: any) => (
                          <SelectInput
                            label="QC Status"
                            id="qc_status"
                            options={Object.values(SlideAdmin.qc_status).map(
                              (p) => ({
                                label: p,
                                value: p,
                              })
                            )}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name="qc_notes">
                        {({ field }: any) => (
                          <TextareaInput
                            label="QC Notes"
                            id="qc_notes"
                            {...field}
                            rows={5}
                          />
                        )}
                      </Field>
                    </fieldset>
                  </>
                )}
              </div>
              <footer>
                <div className="panelContainer__inputGroup">
                  <PathologyWatchButton onClick={onCancel}>
                    Cancel
                  </PathologyWatchButton>
                  {!isNew && (
                    <PathologyWatchButton color="red" onClick={onDelete}>
                      Delete
                    </PathologyWatchButton>
                  )}
                </div>
                {!isNew && (
                  <div className="panelContainer__inputGroup">
                    <span className="actionRequest__icon">
                      {adminSlide.actionRequestState &&
                        (adminSlide.actionRequestState === "loading" ? (
                          <Spinner size="20px" />
                        ) : adminSlide.actionRequestState === "success" ? (
                          <FontAwesomeIcon icon="check" color="var(--green)" />
                        ) : (
                          <FontAwesomeIcon icon="times" color="var(--red)" />
                        ))}
                    </span>
                    <SelectInput
                      id="slide_action"
                      value={slideAction}
                      defaultLabel="Run Action"
                      options={Object.values(slideActions).map(
                        (action: string) => ({
                          label: action.replace("_", " "),
                          value: action,
                        })
                      )}
                      onChange={(e: any) => {
                        setSlideAction(e.target.value);
                        onResetAction();
                      }}
                    />
                    <PathologyWatchButton
                      title=""
                      onClick={() => {
                        if (adminSlide.id && slideAction) {
                          onRunAction({
                            slides: [adminSlide.id],
                            action: slideAction,
                          });
                        } else {
                          onResetAction();
                        }
                      }}
                    >
                      Go
                    </PathologyWatchButton>
                  </div>
                )}
                <div className="panelContainer__inputGroup">
                  <PathologyWatchButton
                    onClick={() => {
                      resetForm();
                    }}
                    disabled={!dirty}
                  >
                    Discard Changes
                  </PathologyWatchButton>
                  <PathologyWatchButton
                    type="submit"
                    color="green"
                    disabled={!dirty}
                  >
                    Save
                  </PathologyWatchButton>
                </div>
              </footer>
            </>
          </AdminPanel>
        </Form>
      )}
    </Formik>
  );
};

export default SlideEditForm;
